var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { EventInjector } from 'react-event-injector';
import { createPrefixCls } from '../_utils/string';
/** 当前动画状态 */
const ANIMATION_STATUS = {
    /** 初始状态 */
    READY: 'ready',
    /** 进场动画执行中 */
    ENTERING: 'entering',
    /** 进场动画已完毕 */
    ENTERED: 'entered',
    /** 离场动画执行中 */
    LEAVING: 'leaving',
    /** 离场动画已完毕 */
    LEAVED: 'leaved'
};
const prefixCls = createPrefixCls('modal');
export const Modal = (_a) => {
    var { children, className, wrapClassName, maskClassName, style, wrapStyle, maskStyle, visible, center, animation = true, animationName = 'slideUp', mask = true, maskClosable = true, allowBodyScroll = false, onClose } = _a, restProps = __rest(_a, ["children", "className", "wrapClassName", "maskClassName", "style", "wrapStyle", "maskStyle", "visible", "center", "animation", "animationName", "mask", "maskClosable", "allowBodyScroll", "onClose"]);
    const [animationStatus, setAnimationStatus] = useState(visible ? ANIMATION_STATUS.ENTERING : ANIMATION_STATUS.READY);
    const show = useMemo(() => {
        return ((animation &&
            [ANIMATION_STATUS.ENTERING, ANIMATION_STATUS.ENTERED, ANIMATION_STATUS.LEAVING].includes(animationStatus)) ||
            (!animation && visible));
    }, [animation, animationStatus, visible]);
    useEffect(() => {
        if (animation) {
            if (visible) {
                if (animationStatus === ANIMATION_STATUS.READY) {
                    setAnimationStatus(ANIMATION_STATUS.ENTERING);
                }
            }
            else {
                if (animationStatus === ANIMATION_STATUS.ENTERED) {
                    setAnimationStatus(ANIMATION_STATUS.LEAVING);
                }
            }
        }
    }, [animation, animationStatus, visible]);
    useEffect(() => {
        if (animationStatus === ANIMATION_STATUS.LEAVED) {
            if (visible) {
                onClose && onClose();
            }
            setAnimationStatus(ANIMATION_STATUS.READY);
        }
    }, [animationStatus, onClose, visible]);
    const handleMaskClick = useCallback(() => {
        if (maskClosable) {
            if (animation) {
                setAnimationStatus(ANIMATION_STATUS.LEAVING);
            }
            else {
                onClose && onClose();
            }
        }
    }, [animation, maskClosable, onClose]);
    const handlePreventDefault = useCallback((e) => {
        e.preventDefault();
    }, []);
    const handleAnimationEnd = useCallback(() => {
        if (animationStatus === ANIMATION_STATUS.ENTERING) {
            setAnimationStatus(ANIMATION_STATUS.ENTERED);
        }
        else if (animationStatus === ANIMATION_STATUS.LEAVING) {
            setAnimationStatus(ANIMATION_STATUS.LEAVED);
        }
    }, [animationStatus]);
    const wrapCls = useMemo(() => {
        return classnames([prefixCls(), wrapClassName]);
    }, [wrapClassName]);
    const isInEnter = useMemo(() => {
        return animation && (animationStatus === ANIMATION_STATUS.ENTERING || animationStatus === ANIMATION_STATUS.ENTERED);
    }, [animation, animationStatus]);
    const isInLeave = useMemo(() => {
        return animation && (animationStatus === ANIMATION_STATUS.LEAVING || animationStatus === ANIMATION_STATUS.LEAVED);
    }, [animation, animationStatus]);
    const maskCls = useMemo(() => {
        return classnames([
            prefixCls('mask'),
            { [prefixCls('mask-animation')]: animation },
            {
                [prefixCls('mask-animation-enter')]: isInEnter
            },
            {
                [prefixCls('mask-animation-leave')]: isInLeave
            },
            maskClassName
        ]);
    }, [animation, isInEnter, isInLeave, maskClassName]);
    const bodyCls = useMemo(() => {
        return classnames([
            prefixCls('body'),
            { [prefixCls('center')]: center },
            { [prefixCls(`animation-${center ? 'center-' : ''}${animationName}`)]: animation },
            {
                [prefixCls(`animation-${center ? 'center-' : ''}${animationName}-enter`)]: isInEnter
            },
            {
                [prefixCls(`animation-${center ? 'center-' : ''}${animationName}-leave`)]: isInLeave
            },
            className
        ]);
    }, [animation, animationName, center, className, isInEnter, isInLeave]);
    const renderBody = useMemo(() => {
        return (React.createElement("div", { className: bodyCls, style: style, onAnimationEnd: handleAnimationEnd }, children));
    }, [bodyCls, children, handleAnimationEnd, style]);
    return ReactDOM.createPortal(show ? (React.createElement("div", Object.assign({ className: wrapCls, style: wrapStyle }, restProps),
        mask && (React.createElement(EventInjector, { settings: { passive: false }, onTouchMove: handlePreventDefault },
            React.createElement("div", { className: maskCls, style: maskStyle, onClick: handleMaskClick }))),
        allowBodyScroll ? (renderBody) : (React.createElement(EventInjector, { settings: { passive: false }, onTouchMove: handlePreventDefault }, renderBody)))) : null, document.body);
};
export default Modal;
